<script lang="ts">
import type { DisplayBlockProps } from '#layers/blocks/types/display-block';
import type { AnyItem, GlobalSingleRelationFieldId } from '#layers/data-model/public';

export interface DisplayRelatedValuesProps extends DisplayBlockProps<AnyItem | AnyItem[]> {
	fieldId: GlobalSingleRelationFieldId;
}

const __DsBlock = {
	type: 'display',
	name: '$t:blocks.display_related_values.name',
	description: '$t:blocks.display_related_values.description',
	icon: 'lucide:link',
	category: 'display',
	hidden: true,
}</script>

<script setup lang="ts">
import { computed } from 'vue';
import { ensureArray, isNullishOrEmpty } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { getRelatedItemDetailRoute } from '#layers/blocks/components/Display/use-related-values-dropdown-items';
import RenderCollectionDisplayTemplate from '#layers/blocks/components/Render/RenderCollectionDisplayTemplate.vue';
import { getCollectionPrimaryKeyFields, useSingleRelationField } from '#layers/data-model/public';
import { useCollectionRoutes } from '#layers/workspace/public';

defineOptions({
	inheritAttrs: false,
});

const props = defineProps<DisplayRelatedValuesProps>();

;

const field = useSingleRelationField(() => props.fieldId);

const isList = computed(() => field.value?.isList ?? false);
const { detailRoute } = useCollectionRoutes(() => field.value?.oppositeCollectionId);
const oppositeCollectionPrimaryKeyFields = computed(() => field.value?.oppositeCollection ? getCollectionPrimaryKeyFields(field.value.oppositeCollection) : []);
const itemDetailRoute = computed(() => {
	if (!props.root || !props.value || Array.isArray(props.value)) {
		return null;
	}

	const primaryKeyFields = oppositeCollectionPrimaryKeyFields.value;

	try {
		return getRelatedItemDetailRoute(props.value, primaryKeyFields, detailRoute) ?? null;
	}
	catch {
		return null;
	}
});
</script>

<template>
	<DisplayTemplateConfigurationError v-if="!field" :error="$t('blocks.display_related_values.error_field_misconfigured')" />
	<DDisplayValueEmpty v-else-if="!value || (Array.isArray(value) && isNullishOrEmpty(value))" />
	<template v-else-if="value && !isList">
		<DTooltip
			v-if="itemDetailRoute"
			:text="$t('blocks.display_related_values.go_to_related_item')"
		>
			<DButton
				variant="ghost"
				color="neutral"
				class="
					group relative -mx-2.5 h-lh w-max max-w-full min-w-0 truncate py-0 pe-8 leading-[inherit] font-normal text-default
				"
				:to="itemDetailRoute"
			>
				<RenderCollectionDisplayTemplate
					:collection-id="field.oppositeCollectionId"
					:item="value"
					:root="false"
					layout="fit-content"
					class="h-full"
				/>
				<span
					class="
						pointer-events-none absolute inset-y-0 inset-e-0 flex w-8 items-center justify-end pe-2.5 opacity-0
						transition-opacity
						group-hover:opacity-100
						group-focus-visible:opacity-100
					"
				>
					<DIcon name="lucide:arrow-up-right" class="size-4" />
				</span>
			</DButton>
		</DTooltip>
		<RenderCollectionDisplayTemplate
			v-else
			:collection-id="field.oppositeCollectionId"
			:item="value"
			:root="false"
			class="h-full"
		/>
	</template>
	<DisplayRelatedValuesDropdown
		v-else
		:item="item!"
		:related-items="ensureArray(value)"
		:field
	/>
</template>
